import { useEffect, useRef, useState } from 'react'
import { getErrorMessage } from '@/utils/getErrorMessage'
import {
  Steps,
  Typography,
  Button,
  Breadcrumb,
  Layout,
  Divider,
  Flex,
  Spin,
  message,
} from 'antd'
import { useNavigate, useLocation, useParams, Outlet } from 'react-router-dom'
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons'
import type { FilterRule, LeadFlow } from '@/data'

interface TestFlow {
  flow_id: string
  flow_name: string
  traffic_percentage: number
}
import { getTest } from '@/api/lop/flow-experiments/getTest'
import { getFlows } from '@/api/lop/lead-flows/getFlows'
import { createTest } from '@/api/lop/flow-experiments/createTest'
import { updateTest } from '@/api/lop/flow-experiments/updateTest'
import type { TestPayload } from '@/api/lop/flow-experiments/createTest'

const { Title } = Typography
const { Content } = Layout

const steps = [
  { title: 'Test Info', path: 'info' },
  { title: 'Filters', path: 'filters' },
  { title: 'Traffic Split', path: 'traffic' },
]

export interface TestEditorContext {
  mode: 'edit' | 'create'
  testId: string | null
  testName: string
  setTestName: (name: string) => void
  baseFlowId: string | null
  setBaseFlowId: (id: string | null) => void
  leadFlows: TestFlow[]
  setLeadFlows: (flows: TestFlow[]) => void
  triggers: FilterRule[]
  setTriggers: (triggers: FilterRule[]) => void
  availableFlows: LeadFlow[]
  basePath: string
}

const TestEditorContent: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams<{ id: string }>()

  const isEditMode = Boolean(id)
  const basePath = isEditMode ? `/tests/${id}/edit` : '/tests/new'

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  const [testName, setTestName] = useState<string>('')
  const [baseFlowId, setBaseFlowId] = useState<string | null>(null)
  const [leadFlows, setLeadFlows] = useState<TestFlow[]>([])
  const [triggers, setTriggers] = useState<FilterRule[]>([])
  const [availableFlows, setAvailableFlows] = useState<LeadFlow[]>([])

  const hasFetched = useRef(false)

  useEffect(() => {
    if (hasFetched.current) return
    hasFetched.current = true
    ;(async () => {
      try {
        setLoading(true)
        const [{ data: flows }, test] = await Promise.all([
          getFlows(),
          id ? getTest(id) : null,
        ])
        setAvailableFlows(flows)
        if (test) {
          setTestName(test.name)
          setBaseFlowId(
            test.base_flow != null ? String(test.base_flow.id) : null,
          )
          setLeadFlows(
            (test.flows ?? [])
              .filter((entry) => entry.flow != null)
              .map((entry) => ({
                flow_id: String(entry.flow!.id),
                flow_name: entry.flow!.name,
                traffic_percentage: entry.traffic_percentage,
              })),
          )
          setTriggers(test.triggers)
        }
      } catch (err) {
        message.error('Failed to load test')
        console.error(err)
      } finally {
        setLoading(false)
      }
    })()
  }, [id])

  const buildPayload = (): TestPayload => ({
    name: testName,
    type: 'lead_flow',
    base_flow_id: baseFlowId != null ? Number(baseFlowId) : null,
    flows: leadFlows.map((f) => ({
      flow_id: f.flow_id,
      traffic_percentage: f.traffic_percentage,
    })),
    ...(triggers.length > 0 && {
      triggers: triggers.map((f, i) => ({
        target: f.target,
        parameter_name: f.parameter_name,
        comparator: f.comparator,
        value: f.value,
        ordinal: i + 1,
      })),
    }),
  })

  const handleSave = async () => {
    try {
      setSaving(true)
      if (isEditMode && id) {
        await updateTest(id, buildPayload())
        message.success('Test updated')
      } else {
        const created = await createTest({
          ...buildPayload(),
          status: 'paused',
        })
        message.success('Test created')
        navigate(`/tests/${created.id}/edit/info`)
      }
    } catch (err) {
      const fallback = isEditMode
        ? 'Failed to update test'
        : 'Failed to create test'
      message.error(getErrorMessage(err, fallback))
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  // Determine current step based on URL path
  const currentStep = steps.findIndex((step) => {
    const fullPath = `${basePath}/${step.path}`
    return (
      location.pathname === fullPath ||
      location.pathname.endsWith(`/${step.path}`)
    )
  })
  const activeStep = currentStep === -1 ? 0 : currentStep

  const handleStepChange = (step: number) => {
    navigate(`${basePath}/${steps[step].path}`)
  }

  const goBack = () => {
    if (activeStep > 0) {
      navigate(`${basePath}/${steps[activeStep - 1].path}`)
    }
  }

  const goNext = () => {
    if (activeStep < steps.length - 1) {
      navigate(`${basePath}/${steps[activeStep + 1].path}`)
    }
  }

  if (loading) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 400 }}>
        <Spin size="large" />
      </Flex>
    )
  }

  const context: TestEditorContext = {
    mode: isEditMode ? 'edit' : 'create',
    testId: id || null,
    testName,
    setTestName,
    baseFlowId,
    setBaseFlowId,
    leadFlows,
    setLeadFlows,
    triggers,
    setTriggers,
    availableFlows,
    basePath,
  }

  return (
    <div>
      <Flex vertical gap="middle" style={{ width: '100%' }}>
        <Breadcrumb
          items={[
            { title: 'Tests', href: '/tests' },
            { title: isEditMode ? `Edit: ${testName}` : 'New Test' },
          ]}
        />

        <Flex align="center" gap="middle">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/tests')}
          />
          <Title level={3} style={{ margin: 0 }}>
            {isEditMode ? `Edit Test: ${testName}` : 'Create New A/B Test'}
          </Title>
        </Flex>

        <Flex gap="large" style={{ marginTop: 16 }}>
          <div
            style={{
              borderRight: '1px solid #f0f0f0',
              paddingRight: 24,
              minWidth: 200,
            }}
          >
            <Steps
              current={activeStep}
              onChange={handleStepChange}
              orientation="vertical"
              items={steps.map((step) => ({
                title: step.title,
              }))}
              style={{ position: 'sticky', top: 24 }}
            />
          </div>
          <Content style={{ flex: 1, minHeight: 400 }}>
            <Outlet context={context} />

            <Divider />

            <Flex justify="space-between" style={{ paddingTop: 16 }}>
              <Button
                onClick={goBack}
                disabled={activeStep === 0}
                icon={<ArrowLeftOutlined />}
              >
                Back
              </Button>

              {activeStep < steps.length - 1 ? (
                <Button type="primary" onClick={goNext}>
                  Next
                  <ArrowRightOutlined />
                </Button>
              ) : (
                <Button type="primary" onClick={handleSave} loading={saving}>
                  {isEditMode ? 'Save Changes' : 'Create Test'}
                </Button>
              )}
            </Flex>
          </Content>
        </Flex>
      </Flex>
    </div>
  )
}

export const TestEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  return <TestEditorContent key={id ?? 'new'} />
}
